import { Helmet } from 'react-helmet-async';
import { useState, useEffect } from 'react';
import {
  Card,
  Stack,
  Container,
  Typography,
  Tooltip,
  Chip,
  Button,
} from '@mui/material';
import Box from '@mui/material/Box';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Cookies from 'universal-cookie';
import { toast } from 'react-toastify';

import Iconify from '../../components/iconify';
import { API_URL } from '../../api';
import HowItWorksCard from '../../components/HowItWorksCard';
import { DataGrid, LoadPanel } from 'devextreme-react';
import {
  Column,
  FilterBuilderPopup,
  FilterPanel,
  FilterRow,
  GroupPanel,
  HeaderFilter,
  Pager,
  Paging,
  Scrolling,
  SearchPanel,
  Button as DevBtn,
} from 'devextreme-react/data-grid';

const cookies = new Cookies();

export default function CourseOrdersPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/course-orders/admin`, {
        headers: {
          Authorization: cookies.get('TOKEN'),
        },
      });
      setOrders(response.data.data || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching course orders:', error);
      toast.error('Failed to load course orders');
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return `£${(price / 100).toFixed(2)}`;
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatCourseDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return 'success';
      case 'pending':
        return 'warning';
      case 'failed':
        return 'error';
      case 'refunded':
        return 'default';
      default:
        return 'default';
    }
  };

  const statusCellRender = (data) => {
    return (
      <Chip
        label={data.value}
        color={getStatusColor(data.value)}
        size="small"
        sx={{ textTransform: 'capitalize' }}
      />
    );
  };

  const amountCellRender = (data) => {
    return formatPrice(data.value);
  };

  const dateCellRender = (data) => {
    return formatDate(data.value);
  };

  const customerCellRender = (data) => {
    const customer = data.data?.customer;
    if (!customer) return '-';
    return (
      <div>
        <div style={{ fontWeight: 500 }}>{customer.parentName}</div>
        <div style={{ fontSize: '0.85em', color: '#666' }}>{customer.email}</div>
      </div>
    );
  };

  const childCellRender = (data) => {
    const customer = data.data?.customer;
    if (!customer) return '-';
    return customer.childFullName;
  };

  const courseDateCellRender = (data) => {
    return formatCourseDate(data.value);
  };

  return (
    <>
      <Helmet>
        <title>Course Orders | English Teacher Admin</title>
      </Helmet>

      <Container>
        <Stack className="stacky">
          <Typography variant="h4" gutterBottom style={{ fontFamily: 'Inter', fontWeight: '600' }}>
            Course Orders
          </Typography>
        </Stack>
        <Box sx={{ mb: 3 }}>
          <HowItWorksCard>
                        <Typography variant="caption" component="div" color="text.secondary">
                            <strong>1.</strong> Course orders are one-off holiday-course sign-ups paid through Stripe on the public site's <em>Courses</em> page.<br />
                            <strong>2.</strong> Each row shows who booked, which course, and payment status.<br />
                            <strong>3.</strong> Click a row to see the full order detail — parent + child info, Stripe reference, confirmation email history.<br />
                            <strong>4.</strong> CSV export at the top for accounting.<br />
                        </Typography>
                    </HowItWorksCard>
        </Box>


        <Card sx={{ borderRadius: '12px' }}>
          <DataGrid
            dataSource={orders}
            showBorders={true}
            autoExpandAll={true}
            columnAutoWidth={true}
            keyExpr="_id"
            wordWrapEnabled={true}
          >
            <LoadPanel enabled={loading} />
            <SearchPanel visible={true} highlightCaseSensitive={true} placeholder="Search course orders..." />
            <FilterRow visible={true} />
            <HeaderFilter visible={true} />
            <GroupPanel visible={true} />
            <FilterPanel visible={true} />
            <FilterBuilderPopup position={{ of: window, at: 'top', my: 'top', offset: { y: 10 } }} />
            <Scrolling mode="standard" />
            <Paging defaultPageSize={20} />
            <Pager
              visible={true}
              allowedPageSizes={[10, 20, 50, 100]}
              showPageSizeSelector={true}
              displayMode="full"
              showInfo={true}
              showNavigationButtons={true}
            />

            <Column dataField="orderNumber" caption="Order #" width={140} />
            <Column dataField="courseTitle" caption="Course" width={180} />
            <Column
              caption="Parent/Guardian"
              cellRender={customerCellRender}
              calculateCellValue={(data) => `${data.customer?.parentName} ${data.customer?.email}`}
              width={200}
            />
            <Column
              caption="Child"
              cellRender={childCellRender}
              calculateCellValue={(data) => data.customer?.childFullName}
              width={150}
            />
            <Column dataField="courseDate" caption="Course Date" cellRender={courseDateCellRender} dataType="date" width={110} />
            <Column dataField="amount" caption="Amount" cellRender={amountCellRender} width={90} />
            <Column dataField="paymentStatus" caption="Status" cellRender={statusCellRender} width={100} />
            <Column dataField="createdAt" caption="Order Date" cellRender={dateCellRender} dataType="datetime" width={150} />

            <Column type="buttons" caption="Actions" width={80}>
              <DevBtn onClick={(e) => navigate(`/course-orders/${e.row?.data?._id}`)}>
                <Button className="btn">
                  <Tooltip title="View Details" placement="top-start" arrow>
                    <Iconify icon="mdi:eye" />
                  </Tooltip>
                </Button>
              </DevBtn>
            </Column>
          </DataGrid>
        </Card>
      </Container>
    </>
  );
}